import type { ComputedEntityDefinition, ModuleContext, PluginDTO } from '@gzl10/nexus-sdk'
import { useTextField, useSelectField, useCheckboxField } from '@gzl10/nexus-sdk/fields'
import { OFFICIAL_PLUGINS } from '@gzl10/nexus-sdk'
import { toPluginDTO } from './plugins.helpers.js'
import { installPluginAction } from './actions/install-plugin.action.js'
import { uninstallPluginAction } from './actions/uninstall-plugin.action.js'
import { togglePluginAction } from './actions/toggle-plugin.action.js'

const allowPluginManagement = process.env['NEXUS_ALLOW_PLUGIN_INSTALL'] !== 'false'

export const pluginsEntity: ComputedEntityDefinition = {
  type: 'computed',
  label: 'Plugins',
  icon: 'mdi:puzzle',
  labelField: 'code',
  routePrefix: '/',
  defaultSort: { field: 'name', order: 'asc' },

  fields: {
    name: useTextField({
      label: { en: 'Name', es: 'Nombre' },
      size: 50,
      nullable: false,
      meta: { sortable: true, searchable: true }
    }),
    code: useTextField({
      label: { en: 'Code', es: 'Código' },
      size: 10,
      nullable: false,
      meta: { sortable: true }
    }),
    label: useTextField({
      label: { en: 'Label', es: 'Etiqueta' },
      size: 100,
      nullable: false,
      meta: { sortable: true }
    }),
    version: useTextField({
      label: { en: 'Version', es: 'Versión' },
      size: 20,
      nullable: false
    }),
    category: useSelectField({
      label: { en: 'Category', es: 'Categoría' },
      options: [
        { value: 'content', label: { en: 'Content', es: 'Contenido' } },
        { value: 'data', label: { en: 'Data', es: 'Datos' } },
        { value: 'assets', label: { en: 'Assets', es: 'Activos' } },
        { value: 'messaging', label: { en: 'Messaging', es: 'Mensajería' } },
        { value: 'jobs', label: { en: 'Jobs', es: 'Trabajos' } },
        { value: 'ai', label: { en: 'AI', es: 'IA' } },
        { value: 'analytics', label: { en: 'Analytics', es: 'Analítica' } },
        { value: 'integrations', label: { en: 'Integrations', es: 'Integraciones' } },
        { value: 'commerce', label: { en: 'Commerce', es: 'Comercio' } },
        { value: 'security', label: { en: 'Security', es: 'Seguridad' } }
      ],
      nullable: true,
      meta: { sortable: true }
    }),
    description: {
      label: { en: 'Description', es: 'Descripción' },
      input: 'markdown',
      localized: true,
      db: { type: 'json' },
      meta: { searchable: true }
    },
    installed: useCheckboxField({
      label: { en: 'Installed', es: 'Instalado' }
    }),
    enabled: useCheckboxField({
      label: { en: 'Enabled', es: 'Habilitado' }
    })
  },

  actions: allowPluginManagement ? [
    { ...installPluginAction, disabled: { field: 'installed', $eq: true } },
    { ...uninstallPluginAction, disabled: { field: 'installed', $eq: false } },
    { ...togglePluginAction, disabled: { field: 'installed', $eq: false } },
  ] : undefined,

  casl: {
    subject: 'SystemPlugin',
    permissions: {
      ADMIN: { actions: ['read', 'execute'] },
      EDITOR: { actions: ['read'] },
      USER: { actions: ['read'] },
      VIEWER: { actions: ['read'] },
      SUPPORT: { actions: ['read'] }
    }
  },

  compute: async (ctx: ModuleContext): Promise<PluginDTO[]> => {
    const discovered = await ctx.core.plugins.discover()
    const discoveredMap = new Map(discovered.map(p => [p.name, p]))
    const states = ctx.core.plugins.getAllStates()

    return OFFICIAL_PLUGINS.map(name => {
      const manifest = discoveredMap.get(name)
      if (manifest) {
        return toPluginDTO(manifest, ctx)
      }
      const short = ctx.core.plugins.shortName(name)
      return {
        name,
        code: short,
        label: short,
        icon: 'mdi:puzzle-outline',
        version: '–',
        description: '',
        installed: false,
        enabled: states[name]?.enabled ?? null,
        modules: [],
      }
    })
  },

  cache: {
    ttl: 60
  }
}
